import React, { useEffect, useState } from 'react';

import { useForm } from 'react-hook-form';
import { Link, useHistory } from 'react-router-dom';
import googleAuthenticatorLogo from 'styles/assets/png/google-authenticator-logo.png';
import microsoftAuthenticatorLogo from 'styles/assets/png/microsoft-authenticator-logo.png';

import { useQueryState } from 'hooks/useQueryState';

import { useLoginStore } from 'store/login.store';

import { useMutationLogin } from 'api/mutations/login.mutation';
import { useQueryGetUserDetail } from 'api/queries/getUserDetail.query';
import { useQueryServerTime } from 'api/queries/serverTime.query';

import { NewButton } from 'components/common/CustomizeButton/newButton';

import { GENERIC_ERROR, encrypt } from 'components/App/AuthPublicPages/Login/login.utils';
import classes from 'components/App/AuthPublicPages/Mfa/mfa.module.scss';
import Layout from 'components/App/AuthPublicPages/_common/publicLayout/layout';
import { useLoginHook } from 'components/App/AuthPublicPages/_hooks/loginHook';
import { CustomizedFormErrorMessage } from 'components/common';

export const Mfa = () => {
    const history = useHistory();
    const [type] = useQueryState('type');
    const [emailAfterMfa, setEmailAfterMfa] = useState<string | undefined>(undefined);
    const [error, setError] = useState<string>('');
    const { data: serverTime } = useQueryServerTime();
    const { username, password } = useLoginStore();
    const { mutate: loginMutation, isLoading: isLoadingLogin } = useMutationLogin();
    const { data: user, isLoading: isLoadingUserDetail } = useQueryGetUserDetail({
        email: emailAfterMfa,
    });

    useLoginHook({ user, isSwitchUser: false });

    const {
        handleSubmit,
        register,
        formState: { errors },
    } = useForm<{ mfaCode: string }>({
        defaultValues: {
            mfaCode: '',
        },
    });

    const handleLogin = ({ mfaCode }: { mfaCode: string }) => {
        if (!username || !password) return null;
        const time = serverTime?.time as number;
        const payload =
            time !== 0
                ? { encrypt: encrypt(JSON.stringify({ username, password, mfaCode }), time), time }
                : { username, password, mfaCode };
        loginMutation(payload, {
            onSuccess: (response) => {
                if (response.email) {
                    setEmailAfterMfa(response.email);
                    setError('');
                } else {
                    setError(GENERIC_ERROR);
                }
            },
            onError: () => {
                setError(GENERIC_ERROR);
            },
        });
    };

    useEffect(() => {
        if (!username || !password) {
            history.push('/login');
        }
    }, [username, password]);

    return (
        <Layout>
            <div className={classes.loginFormWrapper}>
                <div className={classes.loginFormTitleWrapper}>Enter two-factor authentication code</div>
                <p className={classes.subText}>
                    {type === 'email'
                        ? `An email with code has been sent to ${username}`
                        : `Enter the code from ${type} authenticator app`}
                    {type && ['google', 'microsoft'].includes(type) && (
                        <img
                            className={classes.authLogo}
                            src={type === 'google' ? googleAuthenticatorLogo : microsoftAuthenticatorLogo}
                            alt={type}
                        />
                    )}
                </p>
                <form onSubmit={handleSubmit(handleLogin)} autoComplete={'off'}>
                    <div className={classes.formWrapper}>
                        <div className={classes.inputsContainer}>
                            <div className={classes.inputWrapper}>
                                <label htmlFor={'mfaCode'}>Enter your 6-digit code</label>
                                <input
                                    id={'mfaCode'}
                                    className={errors.mfaCode ? classes.invalid : ''}
                                    maxLength={6}
                                    {...register('mfaCode', {
                                        required: 'Code is Required',
                                    })}
                                />
                                {errors.mfaCode && <CustomizedFormErrorMessage text={errors.mfaCode.message} />}
                                {error && <CustomizedFormErrorMessage text={error} />}
                            </div>
                        </div>
                        <p>We're asking for this code based on your two-factor authentication preferences.</p>
                        <div className={classes.actionsWrapper}>
                            <NewButton
                                color={'login'}
                                variant={'contained'}
                                className={classes.submit}
                                disabled={isLoadingLogin || isLoadingUserDetail}
                                loading={isLoadingLogin || isLoadingUserDetail}
                                id={'login-button'}
                                type={'submit'}
                            >
                                Sign In
                            </NewButton>
                        </div>
                        <div className={classes.backToLoginLink}>
                            Having trouble? <Link to={'/login'}>Retry sign in</Link>, or{' '}
                            <a href={'mailto:cs@foresight.works'}>contact us</a> for help
                        </div>
                    </div>
                </form>
            </div>
        </Layout>
    );
};
